import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Bot, User, Trash2, Sparkles } from 'lucide-react';
import ChatWindow from './ChatWindow';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import { chatbotService } from '../../services/chatbotService';
import {
  ChatWidgetContainer,
  ChatButton,
  NotificationDot,
  MessageCountBadge,
  Tooltip,
  ChatWindowContainer
} from './ChatWidget.styles';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [tokenUsage, setTokenUsage] = useState({ used: 0, limit: 20000 });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Load quick questions when widget opens
    if (isOpen) {
      loadQuickQuestions();
      loadTokenUsage();
    }
  }, [isOpen]);

  const loadQuickQuestions = async () => {
    try {
      const questions = await chatbotService.getQuickQuestions();
      setQuickQuestions(questions);
    } catch (error) {
      console.error('Failed to load quick questions:', error);
      // Fallback questions
      setQuickQuestions([
        "How do I join a live session?",
        "What courses are available?",
        "How do I reset my password?",
        "Where can I find course materials?",
        "How do I contact support?"
      ]);
    }
  };

  const loadTokenUsage = async () => {
    try {
      const usage = await chatbotService.getTokenUsage();
      setTokenUsage(usage);
    } catch (error) {
      console.error('Failed to load token usage:', error);
    }
  };

  const handleSendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await chatbotService.sendMessage(message, sessionId);
      
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.response, // Changed from response.reply to response.response
        timestamp: new Date(),
        sources: response.sources || []
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.sessionId) {
        setSessionId(response.sessionId);
      }

      // Update token usage (not applicable for rule-based chatbot)
      if (response.tokenCount) {
        setTokenUsage(prev => ({
          ...prev,
          used: prev.used + response.tokenCount
        }));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm sorry, I'm having trouble processing your request right now. Please try again later.",
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickQuestion = (question) => {
    handleSendMessage(question);
  };

  const handleClearChat = async () => {
    try {
      if (sessionId) {
        await chatbotService.clearSession(sessionId);
      }
      setMessages([]);
      setSessionId(null);
    } catch (error) {
      console.error('Failed to clear chat:', error);
      // Still clear local state even if API fails
      setMessages([]);
      setSessionId(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendMessage(inputMessage);
  };

  const widgetVariants = {
    closed: {
      scale: 1,
      rotate: 0,
      transition: { duration: 0.2 }
    },
    open: {
      scale: 0.9,
      rotate: 180,
      transition: { duration: 0.2 }
    }
  };

  const chatWindowVariants = {
    closed: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: { duration: 0.2 }
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  return (
    <ChatWidgetContainer>
      <AnimatePresence>
        {isOpen && (
          <ChatWindowContainer
            variants={chatWindowVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <ChatWindow
              messages={messages}
              isTyping={isTyping}
              onSendMessage={handleSendMessage}
              onClearChat={handleClearChat}
              onQuickQuestion={handleQuickQuestion}
              quickQuestions={quickQuestions}
              tokenUsage={tokenUsage}
              onClose={() => setIsOpen(false)}
              messagesEndRef={messagesEndRef}
              inputRef={inputRef}
              inputMessage={inputMessage}
              setInputMessage={setInputMessage}
              handleSubmit={handleSubmit}
            />
          </ChatWindowContainer>
        )}
      </AnimatePresence>

      {/* Chat Button */}
      <ChatButton
        onClick={() => setIsOpen(!isOpen)}
        variants={widgetVariants}
        animate={isOpen ? "open" : "closed"}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <Bot size={24} style={{ 
    '@media (max-width: 768px)': { fontSize: '20px' },
    '@media (max-width: 480px)': { fontSize: '18px' },
    '@media (max-width: 360px)': { fontSize: '16px' }
  }} />
        
        {/* Notification Dot */}
        {!isOpen && messages.length === 0 && (
          <NotificationDot
            animate={{
              scale: [1, 1.2, 1],
              opacity: [1, 0.7, 1]
            }}
            transition={{
              duration: 2,
              repeat: 3,
              ease: "easeInOut"
            }}
          />
        )}

        {/* Message Count Badge */}
        {messages.length > 0 && (
          <MessageCountBadge>
            {messages.length > 99 ? '99+' : messages.length}
          </MessageCountBadge>
        )}
      </ChatButton>

      {/* Tooltip */}
      {!isOpen && (
        <Tooltip
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 10 }}
        >
          {messages.length > 0 ? 'Continue chatting' : 'Ask me anything!'}
          <div />
        </Tooltip>
      )}
    </ChatWidgetContainer>
  );
};

export default ChatWidget;
